// The SCREEN CHROME primitives — the toolbar-led screen frame every dashboard surface renders inside,
// recreated 1:1 from the handoff (brand/ritona-app-handoff app.css .toolbar/.tb-*/.subtabs/.screen-body and
// app.js renderScreen): a sticky 52px toolbar carrying the screen name (the ONE h1) and its controls, an
// optional sub-tab bar, and the padded screen body. The prototype's screens are toolbar-led "software
// instruments", not headline-led pages — this file is what makes every surface read that way.
//
// All styling comes from the ported handoff stylesheet (styles/handoff.css) via the prototype's own class
// vocabulary; the classes reference governed tokens only (G-181). The toolbar's h1 carries
// id="surface-heading" so the shell's <section aria-labelledby> keeps naming the region.

import type { ReactNode } from 'react';
import { Icon } from './Icon.tsx';

/** The sticky screen toolbar: the screen name (h1) + right-aligned controls (filters/stamps/info). */
export function ScreenToolbar({
  title,
  voice = false,
  children,
}: {
  readonly title: string;
  /** True renders the name in the voice (Spectral) role — the Today greeting only (prototype .tb-name.voice). */
  readonly voice?: boolean;
  /** The right-side toolbar controls (TbFilter/TbLive/TbStamp/TbInfo/…), in prototype order. */
  readonly children?: ReactNode;
}): React.JSX.Element {
  return (
    <div className="toolbar">
      <h1 className={voice ? 'tb-name voice' : 'tb-name'} id="surface-heading">
        {title}
      </h1>
      <div className="tb-controls">{children}</div>
    </div>
  );
}

/** The padded screen content region under the toolbar (prototype .screen-body). */
export function ScreenBody({ children }: { readonly children: ReactNode }): React.JSX.Element {
  return <div className="screen-body">{children}</div>;
}

/** A toolbar filter chip (prototype .tb-filter) — a labelled toggle with an optional mono count. */
export function TbFilter({
  label,
  on = false,
  count,
  dotTone,
  onClick,
}: {
  readonly label: string;
  readonly on?: boolean;
  readonly count?: number;
  /** An optional leading status dot (the prototype's Awaiting/Attention filters carry a pending dot). */
  readonly dotTone?: 'pending' | 'go';
  readonly onClick?: () => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      className={on ? 'tb-filter on' : 'tb-filter'}
    >
      {dotTone !== undefined ? <span className={`dot ${dotTone}`} aria-hidden="true" /> : null}
      {label}
      {count !== undefined ? <span className="cnt">{count}</span> : null}
    </button>
  );
}

/** The live-status marker (prototype .tb-live) — a Forest dot + LIVE label; the read is freshly fetched. */
export function TbLive({ label = 'Live' }: { readonly label?: string }): React.JSX.Element {
  return (
    <span className="tb-live">
      <span className="dot" aria-hidden="true" /> {label}
    </span>
  );
}

/** A quiet mono timestamp/figure in the toolbar (prototype .tb-stamp). */
export function TbStamp({ children }: { readonly children: ReactNode }): React.JSX.Element {
  return <span className="tb-stamp">{children}</span>;
}

/** The hairline divider between toolbar control groups (prototype .tb-divider). */
export function TbDivider(): React.JSX.Element {
  return <span className="tb-divider" aria-hidden="true" />;
}

/** The screen info affordance (prototype .tb-info + .tb-tip) — a hover tooltip stating what the screen is. */
export function TbInfo({
  title,
  tip,
}: {
  readonly title: string;
  readonly tip: string;
}): React.JSX.Element {
  return (
    <div className="tb-info-wrap">
      <button type="button" className="tb-info" aria-label={`About ${title}`}>
        <Icon name="info" size={17} />
      </button>
      <div className="tb-tip" role="tooltip">
        {tip}
      </div>
    </div>
  );
}

/** One sub-tab definition (id + label + optional honest count badge). */
export interface ScreenSubTab {
  readonly id: string;
  readonly label: string;
  readonly badge?: number | undefined;
}

/** The sub-tab bar under the toolbar (prototype .subtabs/.subtab/.sb). Full-bleed, hairline underline. */
export function ScreenSubTabs({
  label,
  tabs,
  active,
  onSelect,
}: {
  readonly label: string;
  readonly tabs: readonly ScreenSubTab[];
  readonly active: string;
  readonly onSelect: (id: string) => void;
}): React.JSX.Element {
  return (
    <div role="tablist" aria-label={label} className="subtabs">
      {tabs.map((tab) => {
        const on = tab.id === active;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={on}
            data-subtab={tab.id}
            onClick={() => onSelect(tab.id)}
            className={on ? 'subtab on' : 'subtab'}
          >
            {tab.label}
            {tab.badge !== undefined && tab.badge > 0 ? (
              <span className="sb">{tab.badge}</span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
